import {t, tc} from '@/i18n'
import Idea from '@/types/Idea'
import {formulaProblemMessage} from '@/utils/formulaErrors'
import {FieldFailure} from '@/utils/fieldValidation'
import {identifierRefusalText} from '@/utils/propertyDefinition'

/**
 * O nome do campo para a frase, e o alias quando ele não está na coleção — ao CRIAR um
 * atributo, por exemplo, a recusa chega antes de ele existir nela.
 *
 * A coleção é opcional porque quem pergunta já não é só tela de item: a de acesso não tem
 * coleção nenhuma, e as recusas que ela recebe não precisam dela.
 */
function propertyName(alias: string, idea?: Idea): string {
    const property = ((idea && idea.properties) || []).find(candidate => candidate.alias === alias)
    return property ? property.name : alias
}

/**
 * A frase de uma falha de campo, sem o nome dele — no formulário ela aparece embaixo do campo.
 *
 * A mesma frase, venha a falha de onde vier: da conferência do próprio cliente, da prévia (o
 * único) ou da recusa do servidor ao gravar. As três têm a mesma forma, e a frase tem um dono.
 *
 * No único, o dono do valor só aparece quando o campo não faz parte do identificador: se faz, o
 * identificador do outro item repetiria o próprio valor.
 */
export function fieldFailureMessage(failure: FieldFailure, idea?: Idea): string {
    const params = failure.params || {}
    switch (failure.validation) {
        case 'required': return t('required-message')
        case 'url': return t('invalid-url-message')
        //tc: a frase tem singular e plural
        case 'min-items': return tc('validation.min-items', params.count, {count: params.count})
        case 'max-items': return tc('validation.max-items', params.count, {count: params.count})
        //A mensagem que o designer escreveu na regra; sem ela, a padrão (decisão do usuário)
        case 'rule': return params.message || t('validation.rule-default')
        //A opção que entrou sem passar nas "Opções permitidas" — o formulário nem a oferece; chega
        //pelo arrasto do Kanban ou pelo quadro. A orientação do filtro, quando há, como na regra
        case 'option': return params.message || t('validation.option', {value: params.value})
        //A pessoa que entrou num atributo de usuário sem ser membro admitido do ambiente — o campo nem
        //a oferece; chega pelo arrasto do Kanban, ou de quem saiu entre abrir a tela e salvar
        case 'not-member': return t('validation.not-member', {value: params.value})
        //Um vínculo a um item que quem grava não vê — pelo id: o nome do oculto não se mostra
        case 'not-visible': return t('validation.not-visible', {value: params.value})
        //O "Calculado quando" com a condição em Sim — o formulário nem o manda; chega pelo arrasto do
        //Kanban ou pela API
        case 'computed': return t('validation.computed')
        //A mãe que poria o item dentro da própria árvore — a tarefa que vira subtarefa de uma subtarefa dela
        case 'tree-cycle': return t('validation.tree-cycle')
        case 'unique': {
            const property = ((idea && idea.properties) || []).find(candidate => candidate.alias === failure.property)
            //Sem dono também: a marca do item já gravado (5c) diz só que o valor se repete
            return (property && property.identifier) || !params.owner
                ? t('validation.already-used', {value: params.value})
                : t('validation.already-used-by', {value: params.value, owner: params.owner})
        }
    }
}

/**
 * A frase de uma falha onde quer que ela apareça: a do campo e, quando a falha é do OUTRO lado, com o
 * item de lá na frente — `Compositor "Noel Rosa" — Canções: No máximo 3 canções` —, que é o que a
 * diz de quem é: no formulário ela aparece embaixo do campo daqui por onde a gravação chegou lá.
 */
export function failureText(failure: FieldFailure, idea?: Idea): string {
    const message = fieldFailureMessage(failure, idea)
    if (!failure.item) return message
    return t('validation.field-failure', {property: otherItemField(failure.item), message})
}

/** O campo do OUTRO lado, com o item de lá na frente: `Compositor "Noel Rosa" — Canções`. */
function otherItemField(item: NonNullable<FieldFailure['item']>): string {
    return t('validation.other-item-field', {idea: item.idea, item: item.identifier, property: item.propertyName})
}

/**
 * Uma falha de campo onde não há campo para pô-la: de que campo é, e a frase. `ofOtherItem` na do item
 * do OUTRO lado de um relacionamento — editar este item não a resolve.
 */
export type FieldRefusal = { field: string, message: string, ofOtherItem: boolean }

/**
 * As falhas de campo de uma recusa, para quem não tem os campos na tela — o Kanban, a situação, a
 * janela de recusa (ver `refuse`), que as mostra como o formulário: o nome do campo e a frase embaixo.
 * Vazio quando a recusa não é de campo.
 *
 * O obrigatório vazio do próprio item tem frase própria, que diz o que fazer: quem arrasta um card
 * não está vendo o campo. Era do Kanban, que a montava à mão; vale para todo lugar sem campo.
 */
export function fieldRefusals(err: any, idea?: Idea): FieldRefusal[] {
    if (!err || !err.data || err.data.validation !== 'fields') return []
    return (err.data.property as FieldFailure[] || []).map(failure => failure.item
        ? {field: otherItemField(failure.item), message: fieldFailureMessage(failure, idea), ofOtherItem: true}
        : {
            field: propertyName(failure.property, idea),
            message: failure.validation === 'required' ? t('validation.required-empty') : fieldFailureMessage(failure, idea),
            ofOtherItem: false
        })
}

/**
 * A recusa do servidor em português — ou null, quando ela não é uma das que sabemos explicar.
 *
 * Quem chama devolve `null` ao seu tratamento de sempre: o erro genérico continua existindo, e
 * esta função só tira dele os casos em que há algo a fazer.
 *
 * **Mora fora das telas porque são duas**: o formulário de edição e o Kanban, que agora grava
 * relacionamento reverso ao arrastar um card e topa com as mesmas guardas. Escrita duas vezes,
 * a segunda cópia esqueceria um dos casos — e o usuário veria "Erro inesperado" sem saber que
 * bastava reescolher um campo.
 */
export function saveErrorMessage(err: any, idea?: Idea): string | null {
    const validation = err && err.data && err.data.validation
    if (!validation) return null

    //As validações de campo, todas de uma vez numa linha — o formulário as põe em cada campo (ver
    //`InstanceInput.showFailures`), e a janela de recusa uma embaixo da outra (`fieldRefusals`); aqui
    //é para a barra de aviso
    if (validation === 'fields') {
        return fieldRefusals(err, idea)
            .map(({field, message}) => t('validation.field-failure', {property: field, message}))
            .join(' · ')
    }

    //O ambiente foi encerrado depois de a tela ter aberto — ou alguém está com um pacote antigo
    //em cache, onde os botões ainda existem. A tela não tem como adivinhar sozinha: quem sabe é
    //o portão do servidor, e é ele quem manda este `validation`.
    if (validation === 'workspace-closed') return t('workspace-closed-blocked') as string

    //As duas do nível de acesso, do mesmo portão: o nível não alcança esta escrita, e a última saída
    //de administrador do ambiente. A primeira acontece com a tela desatualizada — o nível mudou
    //enquanto ela estava aberta, e os botões de lá ainda são os de antes.
    if (validation === 'level-forbidden') return t('level-forbidden-blocked') as string
    if (validation === 'last-admin') return t('last-admin-blocked') as string
    //Dar a alguém um papel que o ambiente não tem: a lista de papéis mudou com a tela aberta
    if (validation === 'unknown-role') return t('unknown-role-blocked') as string
    //Reordenar as propriedades de uma coleção cuja lista mudou com a tela aberta: outra pessoa criou ou
    //excluiu uma propriedade, e a ordem que se arrastou não fala mais de todas
    if (validation === 'property-order') return t('property-order-blocked') as string
    //O campo que não pode compor o identificador: o servidor confere de novo o que a tela já barra
    //(pedido do usuário), e diz qual e por quê — e, quando é a fórmula, o que ela lê
    if (validation === 'identifier') {
        const [alias, blocker, read] = err.data.property || []
        if (!blocker) return null
        return identifierRefusalText(propertyName(alias, idea), blocker, read)
    }

    //As do acesso por item: a fórmula da coleção não deixa quem pede mexer neste item. A tela nem
    //oferece o botão; quem topa com isto é a tela desatualizada — o acesso mudou com ela aberta —, ou
    //a criação, cujo `creatable` pode depender do que se preencheu
    if (validation === 'not-editable') return t('access.not-editable') as string
    if (validation === 'not-deletable') return t('access.not-deletable') as string
    if (validation === 'not-creatable') return t('access.not-creatable') as string
    if (validation === 'not-visible') return t('access.not-visible') as string
    //E a de um campo que quem pede não edita: o formulário o trava; quem topa com isto é a tela
    //desatualizada, ou o arrasto pela dimensão de um campo que travou com a tela aberta
    if (validation === 'not-editable-field') {
        return t('access.not-editable-field', {property: propertyName(err.data.property, idea)}) as string
    }

    //As do portão de admissão, que é outra escada: entrar no lunedi, e administrar os convites.
    //A primeira reusa a frase que o login já mostra desde sempre.
    if (validation === 'not-approved') return t('auth.not-approved') as string
    if (validation === 'not-approver') return t('not-approver-blocked') as string
    if (validation === 'last-approver') return t('last-approver-blocked') as string

    //Vínculo vazio: um buraco na lista de um relacionamento. O cliente não manda mais, mas a
    //recusa vale para qualquer um — inclusive um com o pacote antigo em cache.
    if (validation === 'empty-link') {
        return t('empty-link-blocked', {property: propertyName(err.data.property, idea)}) as string
    }

    //Valor mandado para um campo calculado. A tela não oferece mais o campo para edição; quem
    //topa com isto é um pacote antigo em cache, ou o Kanban arrastando para uma dimensão velha.
    if (validation === 'computed') {
        return t('computed-blocked', {property: propertyName(err.data.property, idea)}) as string
    }

    //O reverso de um relacionamento calculado. A tela não o oferece para espelhar; quem topa com
    //isto foi mais rápido que a lista — o outro lado virou calculado com o diálogo aberto
    if (validation === 'mirror-computed') return t('mirror-computed-blocked') as string

    //O valor padrão fixo que não é do tipo do campo, ou o padrão num campo que não o tem (calculado,
    //reverso, mídia). A janela não oferece nenhum dos dois; quem topa com isto é a tela desatualizada
    if (validation === 'default') {
        const [alias, reason] = err.data.property || []
        const property = propertyName(alias, idea)
        return (reason === 'type' ? t('default-blocked.type', {property}) : t('default-blocked.unsupported', {property})) as string
    }

    //A fórmula recusada ao salvar o atributo: o servidor manda o código do problema e um
    //detalhe, e a frase é a mesma que o editor mostra — ver formulaProblemMessage.
    if (validation === 'formula') {
        const [, problem] = err.data.property || []
        if (!problem) return null
        return formulaProblemMessage(problem)
    }

    //As do reverso: tirar um vínculo obrigatório, trazer o que já é de outro com o campo de lá obrigatório
    //(a lista nem o oferece; chega aqui por outro caminho), e mexer no que compõe a chave do outro lado. O
    //servidor manda junto o nome do campo e o da coleção de lá, que é o que a frase usa.
    const key = validation === 'reverse-required' ? 'reverse-remove-blocked'
        : validation === 'reverse-taken' ? 'reverse-taken-blocked'
            : validation === 'reverse-identifier' ? 'reverse-identifier-blocked'
                : null
    if (!key) return null
    const about = err.data.property && err.data.property[1]
    if (!about) return null
    return t(key, {property: about.reverseName, idea: about.ideaName}) as string
}
